import { useState, useMemo } from 'react';
import { Container, Title, Paper, Group, Badge, Text, Modal, Stack, Button, Textarea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useDeliveryCalendar, useRequestReschedule } from '@/hooks/usePortalApi';

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'blue',
  in_transit: 'orange',
  completed: 'green',
  delivered: 'green',
  missed: 'red',
};

export default function DeliveryCalendar() {
  const today = new Date();
  const [startDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [endDate] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  const { data, isLoading } = useDeliveryCalendar(
    startDate.toISOString().split('T')[0],
    endDate.toISOString().split('T')[0],
  );
  const rescheduleMut = useRequestReschedule();
  const [rescheduleOpened, { open: openReschedule, close: closeReschedule }] = useDisclosure(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [newDate, setNewDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');

  const events = data?.events ?? [];

  const grouped = useMemo(() => {
    const map: Record<string, typeof events> = {};
    for (const e of events) {
      const d = e.date ?? 'unknown';
      (map[d] ??= []).push(e);
    }
    return map;
  }, [events]);

  const handleReschedule = () => {
    if (!selectedOrderId || !newDate) return;
    rescheduleMut.mutate({ order_id: selectedOrderId, new_date: newDate.toISOString().split('T')[0], notes: notes || undefined }, {
      onSuccess: () => { closeReschedule(); notifications.show({ title: 'Requested', message: 'Reschedule request submitted', color: 'green' }); },
    });
  };

  return (
    <Container size="lg" py="md">
      <Title order={2} mb="md">Delivery Calendar</Title>

      <Group mb="sm" gap="xs">
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <Badge key={status} color={color} variant="dot">{status}</Badge>
        ))}
      </Group>

      {Object.entries(grouped).sort().map(([dateStr, evts]) => (
        <Paper key={dateStr} p="sm" mb="xs" withBorder>
          <Text fw={600} mb="xs">{dateStr}</Text>
          {evts.map((e) => (
            <Group key={e.id} justify="space-between" mb={4}>
              <Group gap="xs">
                <Badge color={STATUS_COLORS[e.status] ?? 'gray'} size="sm">{e.status}</Badge>
                <Text size="sm">{e.so_number ?? `Order #${e.order_id}`}</Text>
              </Group>
              <Group gap="xs">
                <Text size="xs" c="dimmed">{e.planned_arrival ? new Date(e.planned_arrival).toLocaleTimeString() : ''}</Text>
                {e.order_id && (
                  <Button size="xs" variant="subtle" onClick={() => { setSelectedOrderId(e.order_id!); openReschedule(); }}>Reschedule</Button>
                )}
              </Group>
            </Group>
          ))}
        </Paper>
      ))}

      {events.length === 0 && !isLoading && (
        <Text c="dimmed" ta="center" py="xl">No deliveries this month</Text>
      )}

      <Modal opened={rescheduleOpened} onClose={closeReschedule} title="Request Reschedule">
        <Stack>
          <DatePickerInput label="New Delivery Date" value={newDate} onChange={setNewDate} minDate={new Date()} />
          <Textarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
          <Button onClick={handleReschedule} loading={rescheduleMut.isPending}>Submit Request</Button>
        </Stack>
      </Modal>
    </Container>
  );
}
